import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Editor, EditorTools } from "@progress/kendo-react-editor";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "@progress/kendo-theme-default/dist/all.css";
import {
  getDraftEmails,
  getDraftEmail,
  updateDraftEmail,
  updateDraftEmailStatus,
  deleteDraftEmail,
  getEmailTemplateList,
  getEmailTemplateTypes,
  getEmailTemplateFields,
  getTemplateAttachments,
} from "../services/adminEmail_service";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import { useLocation, useNavigate } from "react-router-dom";
import editIcon from "../../../assets/images/grid_edit.gif"
import { toast } from "react-toastify";
import useAuthStore from "../../../store/authStore";

const {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  FontName,
  FontSize,
  FormatBlock,
  AlignLeft,
  OrderedList,
  UnorderedList,
  Indent,
  Outdent,
  Link,
  Unlink,
  InsertImage,
  Undo,
  Redo,
  ViewHtml,
} = EditorTools;


const toInputDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const mm = `${d.getMonth() + 1}`.padStart(2, "0");
  const dd = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const toInputTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const hh = `${d.getHours()}`.padStart(2, "0");
  const mm = `${d.getMinutes()}`.padStart(2, "0");
  return `${hh}:${mm}`;
};

const toMvcDateTime = (dateStr, timeStr) => {
  if (!dateStr) return "";

  const time = timeStr || "00:00";
  return `${dateStr}T${time}:00Z`;
};

export default function DraftTab({ isActive = true }) {

useDocumentTitle("Email Draft");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [pageSize,setPageSize] = useState(15);
  const [selectedRow, setSelectedRow] = useState(null);
  const [draftTemplates, setDraftTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("<p></p>");
  const [sendDate, setSendDate] = useState("");
  const [sendTime, setSendTime] = useState("");
  const [isAttachment, setIsAttachment] = useState(false);
  const [documents, setDocuments] = useState([]);
  const userId = useAuthStore((s) => s.user);
  const location = useLocation();
  const navigate = useNavigate();

  const returnedEmailRef = React.useRef(location.state?.returnedEmail);
  

  const loadGrid = useCallback(async () => {
    setError("");
    try {
      const response = await getDraftEmails("1");
      const raw = response?.data?.data ?? response?.data ?? response;
      const data = Array.isArray(raw) ? raw : raw ? [raw] : [];
      setRows(data);
    } catch (e) {
      setRows([]);
      setError("Failed to load draft emails.");
    }
  }, []);

  const loadDraftMeta = useCallback(async () => {
    try {
      await Promise.all([
        getEmailTemplateTypes(),
        getEmailTemplateFields(),
        getTemplateAttachments(),
      ]);

      const templateListRes = await getEmailTemplateList();
      const templates = templateListRes?.data?.data ?? templateListRes?.data ?? templateListRes;

      setDraftTemplates(Array.isArray(templates) ? templates : []);

      if (Array.isArray(templates) && templates.length > 0) {
        const defaultItem = templates.find((t) => t.isDefault) || templates[0];
        setSelectedTemplateId(String(defaultItem.templateId));
        setFromEmail(defaultItem.fromEmailAddress || "");
        setSubject(defaultItem.emailSubject || "");
        setBody(defaultItem.emailTemplate || "<p></p>");
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      loadGrid();
      loadDraftMeta();
    }
  }, [isActive, loadGrid, loadDraftMeta]);



  const openDraft = useCallback(async (row) => {
    setError("");
    try {
      const response = await getDraftEmail(row.emailId, row.templateId === 0? null: row.templateId);
      const data = response?.data?.data ?? response?.data ?? response;

      setSelectedRow(row);
      setSelectedTemplateId(String(data?.templateId || ""));
      setFromEmail(data?.from || "");
      setSubject(data?.subject || "");
      setBody(data?.body || "<p></p>");
      setSendDate(toInputDate(data?.sendDate));
      setSendTime(toInputTime(data?.sendTime || data?.sendDate));
      setIsAttachment(!!data?.isAttachment);

      const docList = Array.isArray(data?.documentList) ? data.documentList : [];
      setDocuments(docList);
    } catch (e) {
      setError("Failed to load draft details.");
    }
  }, []);

  useEffect(() => {
  const emailId = returnedEmailRef.current;

  if (!emailId) return;
  if (!rows.length) return;

  const match = rows.find((item) => item.emailId === emailId);

  if (match) {
    openDraft(match);

    // clear ref so it won't run again
    returnedEmailRef.current = null;

    navigate(".", { replace: true, state: {} });
  }
}, [rows, openDraft, navigate]);

  const handleSave = useCallback(async () => {
    if (!selectedRow) return;
    if (!sendDate) return setError("Please give send date.");
    if (!sendTime) return setError("Please give send time.");

    try {
      await updateDraftEmail({
        emailId: selectedRow.emailId,
        body,
        subject,
        from: fromEmail,
        status: selectedRow.status,
        sendDate: toMvcDateTime(sendDate, sendTime),
      });
      await loadGrid();
      toast.success("Draft updated successfully")
      setSelectedRow(null);
    } catch (e) {
      toast.error("Failed to update draft email.");
    }
  }, [selectedRow, body, subject, fromEmail, sendDate, sendTime, loadGrid]);

  const handleSend = useCallback(async () => {
    if (!selectedRow) return;
    if (!window.confirm("Send this draft email?")) return;

    try {
      await updateDraftEmailStatus(selectedRow.emailId, 4, userId, isAttachment);
      await loadGrid();
      setSelectedRow(null);
      toast.success('Mail sent successfully')
    } catch (e) {
      toast.error("Failed to send draft email.");
    }
  }, [selectedRow, isAttachment, loadGrid]);

  const handleDelete = useCallback(async () => {
    if (!selectedRow) return;
    if (!window.confirm("This Email will be deleted permanently. Are you sure?")) return;

    try {
      await deleteDraftEmail(selectedRow.emailId);
      await loadGrid();
      setSelectedRow(null);
      toast.success('Draft deleted successfully');
    } catch (e) {
      toast.error("Failed to delete draft email.");
    }
  }, [selectedRow, loadGrid]);

  const onTemplateChange = (e) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    const selected = draftTemplates.find((t) => String(t.templateId) === String(id));
    if (selected) {
      setFromEmail(selected.fromEmailAddress || "");
      setSubject(selected.emailSubject || "");
      setBody(selected.emailTemplate || "<p></p>");
    }
  };

  const formatDateTime = (date) => {
        if (!date) return "";

        const d = new Date(date);

        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const year = d.getFullYear();

        let hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";

        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
    };
  const ActionRenderer = (params) => {
    const row = params.data;
  
    return (
      <div style={{ display: "flex", gap: "8px", marginTop:'10px' }}>  
        <img
          src={editIcon}
          alt="Edit"
          style={{ cursor: "pointer", width: "16px" }}
          onClick={() => openDraft(row)}
        />
      </div>
    );
  };

  const draftColumns = useMemo(
    () => [
      { field: "emailId", hide: true },
      { field: "modifiedBy", hide: true },
      { field: "templateId", hide: true },
      { field: "subject", headerName: "Subject", minWidth: 280, flex: 1.2, sortable: true, filter: true },
      {
        field: "sendDate",
        headerName: "Date",
        minWidth: 190,
        sortable: true,
        filter: true,
        valueFormatter: (p) => formatDateTime(p.value),
      },
      { field: "createdName", headerName: "Created By", minWidth: 160, sortable: true, filter: true },
      { field: "recipientsCount", headerName: "Recipients", minWidth: 130, sortable: true, filter: true },
      { field: "documentCount", headerName: "Documents", minWidth: 130, sortable: true, filter: true },
      {
        headerName: "Action",
        minWidth: 100,
        sortable: false,
        filter: false,
        cellRenderer: ActionRenderer
      },
    ],
    [openDraft]
  );

  const docColumns = useMemo(
    () => [
      { field: "documentId", hide: true },
      { field: "documentName", headerName: "Document Name", minWidth: 240, flex: 1 },
      {
        field: "documentDate",
        headerName: "Document Date",
        minWidth: 160,
        valueFormatter: (p) => formatDateTime(p.value),
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      floatingFilter: true,
    }),
    []
  );

  return (
    <div>
      {error ? <div style={{ color: "red", marginBottom: "12px" }}>{error}</div> : null}

      {!selectedRow ? (
        <div className="ag-theme-alpine" style={{ width: "100%", height: "520px" }}>
          <AgGridReact
            theme="legacy"
            rowData={rows}
            columnDefs={draftColumns}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={15}
            paginationPageSizeSelector={[10, 15, 50, 100, 200]}
            suppressCellFocus={true}
            overlayNoRowsTemplate={"No Records Available"}
            onRowClicked={(e) => openDraft(e.data)}
          />
        </div>
      ) : (
        <div style={{ border: "1px solid #ddd", padding: 12 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div>
              <label>Template</label>
              <select value={selectedTemplateId} disabled onChange={onTemplateChange}>
                <option value="">--Select--</option>
                {draftTemplates.map((t) => (
                  <option key={t.templateId} value={t.templateId}>
                    {t.templateName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label>Send Date</label>
              <input type="date" value={sendDate} onChange={(e) => setSendDate(e.target.value)} />
            </div>

            <div>
              <label>Send Time</label>
              <input type="time" value={sendTime} onChange={(e) => setSendTime(e.target.value)} />
            </div>

            <div>
              <label>From</label>
              <input type="text" value={fromEmail} readOnly />
            </div>

            <div>
              <label>Subject</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={isAttachment}
                onChange={(e) => setIsAttachment(e.target.checked)}
              />
              <label>Attachment</label>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label>Body</label>
            <Editor
              value={body}
              onChange={(e) => setBody(e.html)}
              tools={[
                [Bold, Italic, Underline, Strikethrough],
                [Subscript, Superscript],
                [FontName, FontSize, FormatBlock],
                [AlignLeft],
                [OrderedList, UnorderedList, Indent, Outdent],
                [Link, Unlink, InsertImage],
                [Undo, Redo],
                [ViewHtml],
              ]}
              style={{ height: 300 }}
            />
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button type="button" className="btn btn-primary" onClick={handleSave}>Save</button>
            <button type="button" className="btn btn-primary" onClick={handleSend}>Send</button>
            <button type="button" className="btn btn-primary" onClick={handleDelete}>Delete</button>
            <button type="button" className="btn btn-primary" onClick={() => setSelectedRow(null)}>Back</button>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="ag-theme-alpine" style={{ width: "100%", height: "260px" }}>
              <AgGridReact
                theme="legacy"
                rowData={documents}
                columnDefs={docColumns}
                defaultColDef={defaultColDef}
                suppressCellFocus={true}
                pagination={true}
                paginationPageSize={pageSize}
                paginationPageSizeSelector={[10, 15, 50, 100, 200]}
                overlayNoRowsTemplate={"No Documents"}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
